import {
  AI_PROMPT_BOX_DESCRIPTION,
  AI_PROMPT_BOX_LABEL,
  AI_PROMPT_BOX_PLACEHOLDER,
} from '@/constant/Constants';
import useCodeGenerator from '@/hooks/cbAi/useCodeGenerator';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import { EndpointExtended } from '@context/openapi/types';
import { useExplorer } from '@/hooks/useExplorer';
import { useEffect, useRef, useState } from 'react';
import ExplorerChatBox from '../ExplorerChatBox';
import GeneratedCode from './GeneratedCode';
import { Message, MessageContent } from '@/components/ai-elements/message';
import ConversationBottomInput from '../ConversationBottomInput';
import { AnimatePresence, motion } from 'framer-motion';
import {
  topInputExitVariants,
  conversationContainerVariants,
  messageVariants,
  bottomInputVariants,
} from '../../_assets/animation.variants.const';
import StatusMessage from '@/components/ui/StatusMessage';
import { Plus } from 'lucide-react';
import { useFetchUser } from '@/auth/hooks';
import { isAiBlockedSite } from '@/lib/utils';

interface AICodeGeneratorContainerProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  aiResponseOperations: EndpointExtended[];
}

const AICodeGeneratorContainer: React.FC<AICodeGeneratorContainerProps> = ({
  textareaRef,
  aiResponseOperations,
}) => {
  const { user } = useFetchUser();
  const { siteName } = useExplorer();
  // Check the currently selected site from explorer context, fallback to active_site from user
  const currentSiteName = siteName || user?.active_site?.site_name;
  const isBlocked = isAiBlockedSite(currentSiteName);

  const {
    setUserPrompt,
    messages,
    isGenerating,
    statusMessage,
    resetToHomepage,
    isTypingFollowUp,
  } = useCodeGenerationStore();

  const { setUserPrompt: setExplorerUserPrompt } = useExplorer();

  const { sendMessage, abortStream, clearConversation, isStreamFinished } =
    useCodeGenerator();

  // Smart logic for showing bottom input - avoid glitchy appearance during initial loading only
  const hasCompletedAssistantMessage = messages.some(
    (message) =>
      message.role === 'assistant' && message.content.trim().length > 0
  );

  const shouldShowBottomInput =
    messages.length > 0 && (hasCompletedAssistantMessage || isStreamFinished);

  // Add refs to store previous values for preventing duplicate calls
  const prevPromptRef = useRef<string>('');
  const prevSdkRef = useRef<string | null>(null);

  // Auto-scroll functionality for user messages
  const latestUserMessageRef = useRef<HTMLDivElement>(null);
  const [shouldScrollToUserMessage, setShouldScrollToUserMessage] =
    useState(false);

  // Auto-scroll to latest user message when flag is set
  useEffect(() => {
    if (shouldScrollToUserMessage && latestUserMessageRef.current) {
      // Use longer timeout to ensure animations complete
      setTimeout(() => {
        latestUserMessageRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
        setShouldScrollToUserMessage(false);
      }, 500); // Increased from 100ms to account for staggered animations
    }
  }, [messages, shouldScrollToUserMessage]);

  const handleChatSubmit = (prompt: string, sdk?: string | null) => {
    // Only call sendMessage if either prompt or sdk has changed
    if (prompt !== prevPromptRef.current || sdk !== prevSdkRef.current) {
      setUserPrompt(prompt);
      const isFirstMessage = messages.length === 0;

      // Set scroll flag for follow-up messages (not initial message)
      if (!isFirstMessage) {
        setShouldScrollToUserMessage(true);
      }

      sendMessage(prompt, isFirstMessage, sdk);
      prevPromptRef.current = prompt;
      prevSdkRef.current = sdk ?? null;
    }
  };

  const handleClear = () => {
    abortStream();
    clearConversation();
    setUserPrompt('');
    prevPromptRef.current = '';
    prevSdkRef.current = null;
  };

  const handleNewConversation = () => {
    // Abort any ongoing generation
    abortStream();

    // Reset all AI code generation state
    resetToHomepage();
    prevPromptRef.current = '';
    prevSdkRef.current = null;

    // Clear the explorer context userPrompt (used by the main input field)
    setExplorerUserPrompt('');

    // Clear URL parameters and navigate to clean homepage
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', '/');
      // Scroll to top instantly to show the primary input field and avoid timing issues
      window.scrollTo(0, 0);
    }
  };

  // If the site is blocked, show a disabled message
  if (isBlocked) {
    return (
      <div className="px-[max(1rem,_50%_-_56rem_/_2)] py-10">
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-2xl p-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <svg
                  className="w-6 h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  AI Features Disabled
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  The AI code generation feature has been disabled for your site
                  as requested. You can still use the manual API explorer to
                  test and interact with Chargebee APIs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence mode="wait">
        {/* Show top input only when there are no messages */}
        {messages.length === 0 && (
          <motion.div
            key="top-input"
            initial="show"
            animate="show"
            exit="exit"
            variants={topInputExitVariants}
            className={`px-[max(1rem,_50%_-_56rem_/_2)] isolate z-10`}
          >
            <ExplorerChatBox
              handleChatSubmit={handleChatSubmit}
              onClear={handleClear}
              label={AI_PROMPT_BOX_LABEL}
              description={AI_PROMPT_BOX_DESCRIPTION}
              placeholder={AI_PROMPT_BOX_PLACEHOLDER}
              textareaRef={textareaRef}
            />
          </motion.div>
        )}

        {/* Show conversation when there are messages */}
        {messages.length > 0 && (
          <motion.div
            key="conversation"
            initial="hidden"
            animate="show"
            exit="exit"
            variants={conversationContainerVariants}
            className="pb-40 pt-10"
          >
            <div className="px-[max(1rem,_50%_-_56rem_/_2)]">
              <div className="space-y-6">
                {messages.map((message, index) => {
                  const isLoading =
                    message.role === 'assistant' &&
                    message.content === '' &&
                    isGenerating;

                  // Find the actual latest user message (not assistant loading message)
                  const latestUserMessageIndex = messages.findLastIndex(
                    (m) => m.role === 'user'
                  );
                  const isLatestUserMessage =
                    message.role === 'user' && index === latestUserMessageIndex;

                  return (
                    <motion.div
                      key={message.id}
                      ref={
                        isLatestUserMessage ? latestUserMessageRef : undefined
                      }
                      initial="hidden"
                      animate="show"
                      variants={messageVariants}
                      transition={{
                        delay: index * 0.1,
                        duration: 0.3,
                        ease: [0.4, 0.0, 0.2, 1],
                      }}
                    >
                      <Message
                        from={message.role}
                        timestamp={isLoading ? undefined : message.timestamp}
                      >
                        <MessageContent>
                          {message.role === 'assistant' ? (
                            message.content === '' && isGenerating ? (
                              <>
                                {/* Show status message during generation */}
                                {isGenerating && (
                                  <div className="mb-4">
                                    <StatusMessage status={statusMessage} />
                                  </div>
                                )}
                              </>
                            ) : message.content.trim().length > 0 ? (
                              <GeneratedCode
                                output={message.content}
                                isGenerating={false}
                                aiResponseOperations={aiResponseOperations}
                                showFeedback={false}
                                submitFeedback={() => Promise.resolve(false)}
                              />
                            ) : !isGenerating ? (
                              <div className="text-sm text-gray-500 italic">
                                No response generated. Please try again.
                              </div>
                            ) : null
                          ) : (
                            <div className="whitespace-pre-wrap text-sm bg-white/90 backdrop-blur-sm rounded-lg p-3 border border-gray-100/50 shadow-sm font-sora text-gray-800">
                              {message.content}
                            </div>
                          )}
                        </MessageContent>
                      </Message>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always show bottom input when in conversation mode - control visibility with opacity */}
      {messages.length > 0 && (
        <motion.div
          key="bottom-input"
          initial="hidden"
          animate={shouldShowBottomInput ? 'show' : 'hidden'}
          variants={bottomInputVariants}
          style={{
            pointerEvents: shouldShowBottomInput ? 'auto' : 'none',
          }}
          className="relative"
        >
          {/* Feedback and New conversation buttons - positioned above the input */}
          {shouldShowBottomInput && !isTypingFollowUp && (
            <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-[999999999997] flex items-center justify-center pb-[5.5rem]">
              <div
                className="w-full max-w-[468px] flex justify-center items-center gap-8 px-2"
                style={{ pointerEvents: 'all' }}
              >
                <button
                  onClick={handleNewConversation}
                  className="before:bg-gradient-to-r from-shade-red-500 to-shade-red-800 before:absolute before:inset-0 before:rounded-[inherit] before:p-[2px] before:content-[''] before:mask-custom rounded-[1.25rem] bg-[rgba(255,255,255,0.52)] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] backdrop-blur-0 font-sora flex items-center p-3 px-4 gap-2 text-left font-medium text-[0.875rem] hover:shadow-lg hover:bg-[rgba(255,255,255,0.7)] hover:scale-[1.02] transition-all duration-300 ease-in-out relative"
                  type="button"
                >
                  <Plus className="h-4 w-4 text-shade-red-500" />
                  <span className="bg-gradient-to-r from-shade-red-500 to-shade-red-800 bg-clip-text text-transparent leading-tight font-medium">
                    New
                  </span>
                </button>
              </div>
            </div>
          )}

          <ConversationBottomInput
            onSubmit={handleChatSubmit}
            disabled={isGenerating || !shouldShowBottomInput}
          />
        </motion.div>
      )}
    </>
  );
};
export default AICodeGeneratorContainer;
